import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  SERVICE_CLASSES,
  type PrinterDto,
  type ProductCategoryDto,
  type ServiceClass,
  type TagDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { printerChoiceLabel } from '@/features/settings/printerTest';
import { api, ApiError } from '@/lib/api';
import { useProductCategories, useTags } from './useCafe';

/**
 * What the pickers need of a printer. `isActive` because a switched-off
 * printer routes nothing — its tickets fall to the next rung — and a
 * category pointed at one must not read as if it still printed there.
 */
type PrinterOption = Pick<PrinterDto, 'id' | 'name' | 'isActive'>;

// `ServiceClass` and `SERVICE_CLASSES` are re-exported from `@gc/shared` (the
// one list) so the several modules importing them from this screen keep
// working. They used to be declared here, which is how the dropdown came to
// offer a bucket the API's own copy of the list rejected.
export { SERVICE_CLASSES, type ServiceClass };

/** The API returns it today; the shared DTO gains the field with the lead. */
type CategoryWithClass = ProductCategoryDto & { serviceClass?: ServiceClass };

/**
 * Product Categories and Tags (SPEC §6.2).
 *
 * Two of the four sections of the Categories page. Device Categories and Game
 * Categories belong to other agents' areas and are not rendered here.
 *
 * Both sections carry a Printer column, because this mapping IS the cafe print
 * routing (SPEC §10), highest first: a tag's printer, then the product
 * category's, then the printer of the ORDERING DEVICE's category, then the
 * venue Default Printer (see `apps/api/src/orders/station-routing.ts`). An
 * empty printer here does not mean "the default" any more (2026-09-24): it
 * means the next rung down gets a say, and each section names ITS next rung.
 * A tag with no printer hands the item to its product category — not to the
 * device category — so the Tags section must not borrow the categories'
 * wording: a tag row reading "Device category, then default printer" invites
 * the owner to give the tag a printer to keep the item in the kitchen, and a
 * Tag printer overriding every category is exactly how the whole menu went to
 * the kitchen on 9 Aug. A switched-off printer names nothing at any rung, so
 * it is marked "(off)".
 */
export function TaxonomyPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const manage = can('products.manage');

  const { categories, reload: reloadCategories } = useProductCategories();
  const { tags, reload: reloadTags } = useTags();
  const [printers, setPrinters] = useState<PrinterOption[]>([]);
  const [printersUnavailable, setPrintersUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Printers are owned by the settings area, and the path is `/settings/printers`.
   *
   * THIS READ `/printers`, WHICH IS A 404, AND HAS SINCE THE PAGE WAS WRITTEN.
   * The catch below then did exactly what it was designed to do — disable the
   * column rather than render a dropdown that could silently clear a mapping —
   * so the failure looked like a considered state instead of a broken URL. The
   * effect was that NOTHING could ever be routed to a station printer: every
   * tag and every category was stuck reading "Default Printer", and the whole
   * split-ticket feature was unreachable.
   *
   * Every other caller in the app already used the right path
   * (`DeviceCategoriesPage`, `GeneralSettingsPage`, `PrinterDialog`), which is
   * why the endpoint itself was never suspect.
   */
  useEffect(() => {
    void (async () => {
      try {
        setPrinters(await api.get<PrinterOption[]>('/settings/printers'));
        setPrintersUnavailable(false);
      } catch {
        setPrinters([]);
        setPrintersUnavailable(true);
      }
    })();
  }, []);

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl font-semibold text-content">{t('cafe.taxonomy.title')}</h1>
        <div className="flex-1" />
        <Link
          to="/cafe/products"
          className="rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
        >
          {t('cafe.products.title')}
        </Link>
      </header>

      {error ? (
        <p role="alert" className="px-4 pb-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {/*
        Two different reasons the printer column can be unusable, and they used
        to look identical on screen — which is how a 404 sat here unnoticed,
        reading like a considered state rather than a fault.
      */}
      {printersUnavailable ? (
        <p
          role="alert"
          className="mx-4 mb-3 rounded-control border border-danger/40 bg-input px-3 py-2 text-xs text-danger"
        >
          {t('cafe.taxonomy.printersUnavailable')}
        </p>
      ) : printers.length === 0 ? (
        <p
          data-no-printers
          className="mx-4 mb-3 rounded-control border border-subtle bg-input px-3 py-2 text-xs text-content-muted"
        >
          {t('cafe.taxonomy.noPrinters')}{' '}
          {/* Printing settings, not General: that is where printers live
              since §6.1 was split into four pages. */}
          <Link to="/settings/printing" className="underline hover:text-content">
            {t('cafe.taxonomy.addPrinter')}
          </Link>
        </p>
      ) : null}

      <div className="grid min-h-0 flex-1 gap-4 overflow-auto px-4 pb-4 lg:grid-cols-2">
        <Section
          title={t('cafe.taxonomy.productCategories')}
          hint={t('cafe.taxonomy.categoriesHint')}
          rows={categories.map((category) => ({
            id: category.id,
            name: category.name,
            printerId: category.printerId,
            printerName: category.printerName,
            usageCount: category.productCount,
            hiddenUsageCount: category.hiddenProductCount,
            serviceClass: (category as CategoryWithClass).serviceClass ?? 'CAFE',
          }))}
          printers={printers}
          printersEditable={!printersUnavailable}
          manage={manage}
          // Wave 3 — the picker beside the printer routing: printer says WHERE
          // the item prints, serviceClass says WHICH discount bucket it bills.
          withServiceClass
          onCreate={(name, printerId, serviceClass) =>
            save('/product-categories', { name, printerId, serviceClass })
          }
          onUpdate={(id, name, printerId, serviceClass) =>
            save(`/product-categories/${id}`, { name, printerId, serviceClass }, 'PATCH')
          }
          onDelete={(id) => remove(`/product-categories/${id}`)}
          onChanged={reloadCategories}
        />

        <Section
          title={t('cafe.taxonomy.tags')}
          hint={t('cafe.taxonomy.tagsHint')}
          rows={tags.map((tag) => ({
            id: tag.id,
            name: tag.name,
            printerId: tag.printerId,
            printerName: tag.printerName,
            usageCount: tag.productCount,
            hiddenUsageCount: tag.hiddenProductCount,
          }))}
          printers={printers}
          printersEditable={!printersUnavailable}
          manage={manage}
          emptyPrinterLabel={t('cafe.taxonomy.tagInherits')}
          onCreate={(name, printerId) => save('/product-tags', { name, printerId })}
          onUpdate={(id, name, printerId) =>
            save(`/product-tags/${id}`, { name, printerId }, 'PATCH')
          }
          onDelete={(id) => remove(`/product-tags/${id}`)}
          onChanged={reloadTags}
        />
      </div>
    </div>
  );

  async function save(
    path: string,
    body: { name: string; printerId: string | null; serviceClass?: ServiceClass },
    method: 'POST' | 'PATCH' = 'POST',
  ): Promise<boolean> {
    setError(null);
    try {
      if (method === 'PATCH') await api.patch(path, body);
      else await api.post(path, body);
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('cafe.taxonomy.couldNotSave'));
      return false;
    }
  }

  async function remove(path: string): Promise<boolean> {
    setError(null);
    try {
      await api.delete(path);
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('cafe.taxonomy.couldNotSave'));
      return false;
    }
  }
}

interface SectionRow {
  id: string;
  name: string;
  printerId: string | null;
  printerName: string | null;
  usageCount: number;
  /**
   * Sold-and-hidden products still filed here. Shown beside the visible count
   * because they are otherwise invisible everywhere — they used to be folded
   * into `usageCount`, producing a category that claimed a product it never
   * showed. They no longer block a delete.
   */
  hiddenUsageCount: number;
  /** Present only on product categories (Wave 3). */
  serviceClass?: ServiceClass;
}

/** "3" on its own, or "0 + 1 hidden" when something is filed but invisible. */
function UsageCount({ visible, hidden }: { visible: number; hidden: number }) {
  const { t } = useTranslation();
  return (
    <span>
      {visible}
      {hidden > 0 ? (
        <span className="ps-1 text-xs text-content-muted">
          {t('cafe.taxonomy.plusHidden', { times: hidden })}
        </span>
      ) : null}
    </span>
  );
}

export function Section({
  title,
  hint,
  rows,
  printers,
  printersEditable,
  manage,
  withServiceClass = false,
  emptyPrinterLabel,
  onCreate,
  onUpdate,
  onDelete,
  onChanged,
}: {
  title: string;
  hint: string;
  rows: SectionRow[];
  printers: PrinterOption[];
  printersEditable: boolean;
  manage: boolean;
  /** Adds the CAFE/KITCHEN discount-bucket column (categories only). */
  withServiceClass?: boolean;
  /**
   * What a row with no printer falls to — the NEXT rung of the routing, which
   * differs by section (see the page comment). Defaults to the product
   * categories' wording; the Tags section passes its own.
   */
  emptyPrinterLabel?: string;
  onCreate: (
    name: string,
    printerId: string | null,
    serviceClass?: ServiceClass,
  ) => Promise<boolean>;
  onUpdate: (
    id: string,
    name: string,
    printerId: string | null,
    serviceClass?: ServiceClass,
  ) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const fallbackLabel = emptyPrinterLabel ?? t('cafe.taxonomy.inheritsDefault');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftPrinter, setDraftPrinter] = useState('');
  const [draftServiceClass, setDraftServiceClass] = useState<ServiceClass>('CAFE');
  const [adding, setAdding] = useState(false);

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-2 py-1.5 text-sm text-content outline-none focus:border-primary';

  return (
    <section className="min-w-0 rounded-card border border-subtle bg-raised">
      <header className="flex items-center gap-2 border-b border-subtle p-3">
        <h2 className="font-semibold text-content">{title}</h2>
        <div className="flex-1" />
        {manage ? (
          <button
            type="button"
            onClick={startAdd}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-fg hover:bg-primary-hover"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            {t('common.add')}
          </button>
        ) : null}
      </header>

      <p className="px-3 pt-2 text-xs text-content-muted">{hint}</p>

      {/* The table scrolls sideways inside its own card, never the page. */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-content-secondary">
              <th className="px-3 py-2 max-md:px-2 text-start">{t('common.name')}</th>
              <th className="px-3 py-2 max-md:px-2 text-start">{t('cafe.taxonomy.printer')}</th>
              {withServiceClass ? (
                <th className="px-3 py-2 max-md:px-2 text-start">
                  {t('cafe.taxonomy.serviceClass')}
                </th>
              ) : null}
              <th className="px-3 py-2 max-md:px-2 text-end">{t('cafe.taxonomy.products')}</th>
              <th className="px-3 py-2 max-md:px-2" />
            </tr>
          </thead>
          <tbody>
            {adding ? (
              <tr className="bg-row-even">
                <td className="px-3 py-2 max-md:px-2">
                  <input
                    value={draftName}
                    onChange={(event) => setDraftName(event.target.value)}
                    autoFocus
                    className={inputClass}
                  />
                </td>
                <td className="px-3 py-2 max-md:px-2">
                  <PrinterSelect
                    value={draftPrinter}
                    printers={printers}
                    emptyLabel={fallbackLabel}
                    disabled={!printersEditable}
                    onChange={setDraftPrinter}
                  />
                </td>
                {withServiceClass ? (
                  <td className="px-3 py-2 max-md:px-2">
                    <ServiceClassSelect value={draftServiceClass} onChange={setDraftServiceClass} />
                  </td>
                ) : null}
                <td />
                <td className="px-3 py-2 max-md:px-2">
                  <RowActions
                    onConfirm={() => void commitAdd()}
                    onCancel={() => setAdding(false)}
                  />
                </td>
              </tr>
            ) : null}

            {rows.length === 0 && !adding ? (
              <tr>
                <td
                  colSpan={withServiceClass ? 5 : 4}
                  className="px-3 py-4 text-sm text-content-muted"
                >
                  {t('common.noResults')}
                </td>
              </tr>
            ) : null}

            {rows.map((row, index) =>
              editingId === row.id ? (
                <tr key={row.id} className={index % 2 === 0 ? 'bg-row-even' : 'bg-row-odd'}>
                  <td className="px-3 py-2 max-md:px-2">
                    <input
                      value={draftName}
                      onChange={(event) => setDraftName(event.target.value)}
                      autoFocus
                      className={inputClass}
                    />
                  </td>
                  <td className="px-3 py-2 max-md:px-2">
                    <PrinterSelect
                      value={draftPrinter}
                      printers={printers}
                      emptyLabel={fallbackLabel}
                      disabled={!printersEditable}
                      onChange={setDraftPrinter}
                    />
                  </td>
                  {withServiceClass ? (
                    <td className="px-3 py-2 max-md:px-2">
                      <ServiceClassSelect
                        value={draftServiceClass}
                        onChange={setDraftServiceClass}
                      />
                    </td>
                  ) : null}
                  <td className="tabular px-3 py-2 text-end text-content-secondary">
                    <UsageCount visible={row.usageCount} hidden={row.hiddenUsageCount} />
                  </td>
                  <td className="px-3 py-2 max-md:px-2">
                    <RowActions
                      onConfirm={() => void commitEdit(row.id)}
                      onCancel={() => setEditingId(null)}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={row.id} className={index % 2 === 0 ? 'bg-row-even' : 'bg-row-odd'}>
                  <td className="px-3 py-2 max-md:px-2 text-content">{row.name}</td>
                  <td
                    data-printer-cell={row.id}
                    className="px-3 py-2 max-md:px-2 text-content-secondary"
                  >
                    {row.printerName ? (
                      printerChoiceLabel(
                        {
                          name: row.printerName,
                          isActive:
                            printers.find((printer) => printer.id === row.printerId)?.isActive ??
                            true,
                        },
                        t('settings.general.printerOff'),
                      )
                    ) : (
                      <span className="text-content-muted">{fallbackLabel}</span>
                    )}
                  </td>
                  {withServiceClass ? (
                    <td className="px-3 py-2 max-md:px-2 text-content-secondary">
                      {t(`cafe.taxonomy.serviceClasses.${row.serviceClass ?? 'CAFE'}`)}
                    </td>
                  ) : null}
                  <td className="tabular px-3 py-2 text-end text-content-secondary">
                    <UsageCount visible={row.usageCount} hidden={row.hiddenUsageCount} />
                  </td>
                  <td className="px-3 py-2 max-md:px-2">
                    <div className="flex justify-end gap-1">
                      <SmallButton
                        label={t('common.edit')}
                        disabled={!manage}
                        onClick={() => startEdit(row)}
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                      </SmallButton>
                      <SmallButton
                        label={t('common.delete')}
                        disabled={!manage}
                        onClick={() => void commitDelete(row)}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </SmallButton>
                    </div>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  function startAdd(): void {
    setAdding(true);
    setEditingId(null);
    setDraftName('');
    setDraftPrinter('');
    setDraftServiceClass('CAFE');
  }

  function startEdit(row: SectionRow): void {
    setAdding(false);
    setEditingId(row.id);
    setDraftName(row.name);
    setDraftPrinter(row.printerId ?? '');
    setDraftServiceClass(row.serviceClass ?? 'CAFE');
  }

  async function commitAdd(): Promise<void> {
    if (!draftName.trim()) return;
    const serviceClass = withServiceClass ? draftServiceClass : undefined;
    if (await onCreate(draftName.trim(), draftPrinter || null, serviceClass)) {
      setAdding(false);
      onChanged();
    }
  }

  async function commitEdit(id: string): Promise<void> {
    if (!draftName.trim()) return;
    const serviceClass = withServiceClass ? draftServiceClass : undefined;
    if (await onUpdate(id, draftName.trim(), draftPrinter || null, serviceClass)) {
      setEditingId(null);
      onChanged();
    }
  }

  async function commitDelete(row: SectionRow): Promise<void> {
    if (!window.confirm(t('cafe.taxonomy.confirmDelete', { name: row.name }))) return;
    if (await onDelete(row.id)) onChanged();
  }
}

function PrinterSelect({
  value,
  printers,
  emptyLabel,
  disabled,
  onChange,
}: {
  value: string;
  printers: PrinterOption[];
  /** The blank choice: the section's next rung, never a generic "default". */
  emptyLabel: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      title={disabled ? t('cafe.taxonomy.printersUnavailable') : undefined}
      className="w-full rounded-control border border-subtle bg-input px-2 py-1.5 text-sm text-content outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
    >
      <option value="">{emptyLabel}</option>
      {printers.map((printer) => (
        <option key={printer.id} value={printer.id}>
          {printerChoiceLabel(printer, t('settings.general.printerOff'))}
        </option>
      ))}
    </select>
  );
}

/**
 * CAFE / KITCHEN — which discount bucket the category's products bill under
 * at checkout (Wave 3). Exported so the tests can pin the two options.
 */
export function ServiceClassSelect({
  value,
  onChange,
}: {
  value: ServiceClass;
  onChange: (value: ServiceClass) => void;
}) {
  const { t } = useTranslation();
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value as ServiceClass)}
      aria-label={t('cafe.taxonomy.serviceClass')}
      title={t('cafe.taxonomy.serviceClassHint')}
      className="w-full rounded-control border border-subtle bg-input px-2 py-1.5 text-sm text-content outline-none focus:border-primary"
    >
      {SERVICE_CLASSES.map((serviceClass) => (
        <option key={serviceClass} value={serviceClass}>
          {t(`cafe.taxonomy.serviceClasses.${serviceClass}`)}
        </option>
      ))}
    </select>
  );
}

function RowActions({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex justify-end gap-1">
      <SmallButton label={t('common.save')} onClick={onConfirm}>
        <Check className="h-3.5 w-3.5" aria-hidden />
      </SmallButton>
      <SmallButton label={t('common.cancel')} onClick={onCancel}>
        <X className="h-3.5 w-3.5" aria-hidden />
      </SmallButton>
    </div>
  );
}

function SmallButton({
  children,
  label,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="grid h-7 w-7 place-items-center rounded-control border border-subtle text-content-secondary transition-colors hover:bg-hover hover:text-content disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
